import type { BufferGeometry, Object3D, Texture, WebGLRenderer } from 'three';
import { isPublicTutorialRoute } from '../../publicRoute';
import { serverLog, type ServerLog } from './serverLog';

// WHAT THE PAGE'S 3D VIEWS HOLD, in the load log once a second while it changes (Louis 2026-10-26).
// On an iPhone the installed app loads the published tutorial twice, the first copy vanishing
// without a word; iOS tells a page nothing of its own memory, so this is the next best thing:
// what our own code has asked the GPU for. Not the process's total, and blind to images.
//
// Every renderer is counted from its creation (`tallyRenderer`) to `releaseContext`, with the
// scenes it has drawn: one, or several for a renderer the views of a screen share (see
// sharedRenderer). On the GPU each renderer holds its OWN copy of what it draws, so a geometry two
// renderers share counts twice, once per context, as it is uploaded; two scenes of one renderer
// share it once.
const live = new Map<WebGLRenderer, Set<Object3D>>();
let log: ServerLog | null = null;
let last = '';
// 3D views the browser took back since the page opened: a phone short of memory, saying so.
let lost = 0;

// A VIEW'S DRAWING SURFACE, and whether the browser has really let go of it (Louis 2026-10-26).
// Each canvas has the GPU's own memory behind it: its colour and depth for every pixel, four times
// over for the anti-aliasing (4 + 4 bytes, x4), and the final picture (4) — about 36 bytes a pixel,
// some 40 MB for a full-width view on an iPhone. Releasing a view gives the context back, but that
// memory goes only when the browser destroys the canvas itself, which its garbage collector does in
// its own time. They did pile up, so releaseContext now shrinks the canvas first: a released view
// still counts until the browser reports the canvas destroyed (FinalizationRegistry), but at the
// size it was left, one pixel.
const SURFACE_BYTES_PER_PX = 36;
let created = 0;
let nextRelease = 0;
const awaiting = new Map<number, number>();
const destroyed = typeof FinalizationRegistry === 'undefined' ? null
  : new FinalizationRegistry<number>((id) => { awaiting.delete(id); });

export function tallyRenderer(renderer: WebGLRenderer) {
  created += 1;
  live.set(renderer, new Set());
  renderer.domElement.addEventListener('webglcontextlost', () => { if (live.has(renderer)) lost += 1; });
  const render = renderer.render.bind(renderer);
  renderer.render = (scene, camera) => {
    live.get(renderer)?.add(scene);
    render(scene, camera);
  };
  if (log) return;
  log = serverLog('load', isPublicTutorialRoute() ? 'mem-tutorial' : 'mem-app');
  if (log) setInterval(report, 1000);
}

export function untallyRenderer(renderer: WebGLRenderer) {
  if (!live.delete(renderer)) return;
  const canvas = renderer.domElement;
  const id = ++nextRelease;
  awaiting.set(id, canvas.width * canvas.height * SURFACE_BYTES_PER_PX);
  destroyed?.register(canvas, id);
}

// AND WHAT THE PAGE ITSELF HOLDS, shown or not (Louis 2026-10-26), in its own memory beside the
// GPU's, which on an iPhone is the same memory:
// - `kept`: every mesh decoded and still alive, its geometry's arrays and its textures' data, each
//   once however many views or copies use it. Every file the loader reads comes through here
//   (meshLoader), held weakly, so a mesh the page has let go of drops out. It was only the meshes
//   loadFit keeps, which left out every one a scene reads for itself.
// - `files`: the bytes in three's file cache, every file downloaded on the published page (the
//   ones fetched ahead, see meshPreload, and the ones its scenes asked for), kept for its life.
const decoded = new Set<WeakRef<Object3D>>();
let fileCache: { files: Record<string, unknown> } | null = null;
export const tallyDecoded = (scene: Object3D) => {
  if (typeof WeakRef !== 'undefined') decoded.add(new WeakRef(scene));
};
export const tallyFileCache = (cache: { files: Record<string, unknown> }) => { fileCache = cache; };
const cachedBytes = () => Object.values(fileCache?.files ?? {}).reduce<number>((n, f) => n
  + (f instanceof ArrayBuffer ? f.byteLength : typeof f === 'string' ? f.length * 2 : 0), 0);

// The bytes the textures of `objects` hold once decoded, each texture once.
function textureBytes(objects: Iterable<Object3D | null>): number {
  const seen = new Set<Texture>();
  for (const root of objects) {
    root?.traverse((o) => {
      const { material } = o as Object3D & { material?: object | object[] };
      for (const m of Array.isArray(material) ? material : [material]) {
        for (const v of Object.values(m ?? {})) if ((v as Texture | null)?.isTexture) seen.add(v as Texture);
      }
    });
  }
  let n = 0;
  for (const t of seen) n += textureSize(t);
  return n;
}

// A texture's bytes on the GPU: a compressed one's (KTX2, see pack.mjs) are its levels as they
// are, the rest 4 bytes a pixel once decoded.
function textureSize(t: Texture): number {
  const levels = t as Texture & { isCompressedTexture?: boolean; mipmaps?: { data?: ArrayBufferView }[] };
  if (levels.isCompressedTexture) return (levels.mipmaps ?? []).reduce((n, m) => n + (m.data?.byteLength ?? 0), 0);
  const { width = 0, height = 0 } = (t.image ?? {}) as { width?: number; height?: number };
  return width * height * 4;
}

const bytes = (g: BufferGeometry) => Object.values(g.attributes)
  .reduce((n, a) => n + ((a as { array?: { byteLength?: number } }).array?.byteLength ?? 0), g.index?.array.byteLength ?? 0);

// WHAT THE VIEWS HOLD NOW, in MB: on the GPU (`geometry`, one copy per view, and `textures`) and in
// the page itself (`page`, each geometry once). Also read by the tutorial's per-screen report
// (see screenMemory).
// `kept` and `files`: what the page holds whether or not a view shows it (see tallyDecoded). `created`,
// the views made since the page opened; `awaiting` and `awaitingMb`, those released whose canvas the
// browser has not yet destroyed, and the surfaces they still hold (see SURFACE_BYTES_PER_PX).
export type GpuNow = {
  views: number; px: number; geometry: number; page: number; textures: number; lost: number;
  kept: number; files: number; created: number; awaiting: number; awaitingMb: number;
};

export function gpuNow(): GpuNow {
  let px = 0;
  let geometry = 0;
  let textures = 0;
  // And once over the whole page: the page's own copy of a geometry is one, however many views
  // draw it (see meshLoader's shared shapes).
  const inPage = new Set<BufferGeometry>();
  for (const [renderer, scenes] of live) {
    px += renderer.domElement.width * renderer.domElement.height;
    const geos = new Set<BufferGeometry>();
    const texs = new Set<Texture>();
    const addTextures = (holder: object | null | undefined) => {
      for (const v of Object.values(holder ?? {})) if ((v as Texture | null)?.isTexture) texs.add(v as Texture);
    };
    for (const scene of scenes) {
      scene.traverse((o) => {
        const { geometry: g, material } = o as Object3D & { geometry?: BufferGeometry; material?: object | object[] };
        if (g?.isBufferGeometry) geos.add(g);
        for (const m of Array.isArray(material) ? material : [material]) addTextures(m);
      });
      addTextures(scene);   // a Scene's environment and background
    }
    for (const g of geos) {
      inPage.add(g);
      geometry += bytes(g);
    }
    for (const t of texs) textures += textureSize(t);
  }
  const page = [...inPage].reduce((n, g) => n + bytes(g), 0);
  const alive: Object3D[] = [];
  for (const ref of decoded) {
    const scene = ref.deref();
    if (scene) alive.push(scene);
    else decoded.delete(ref);
  }
  const keptGeometry = new Set<BufferGeometry>();
  for (const root of alive) {
    root.traverse((o) => {
      const g = (o as Object3D & { geometry?: BufferGeometry }).geometry;
      if (g?.isBufferGeometry) keptGeometry.add(g);
    });
  }
  const keptBytes = [...keptGeometry].reduce((n, g) => n + bytes(g), 0) + textureBytes(alive);
  return {
    views: live.size, px: px / 1e6, geometry: geometry / 1e6, page: page / 1e6, textures: textures / 1e6, lost,
    kept: keptBytes / 1e6, files: cachedBytes() / 1e6,
    created, awaiting: awaiting.size, awaitingMb: [...awaiting.values()].reduce((n, b) => n + b, 0) / 1e6,
  };
}

function report() {
  const now = gpuNow();
  const mb = (n: number) => n.toFixed(1);
  const line = `mem ${now.views} 3D views, canvases ${now.px.toFixed(2)} Mpx, geometry ${mb(now.geometry)} MB (${mb(now.page)} MB in the page), textures ${mb(now.textures)} MB, page holds ${mb(now.kept)} MB decoded and ${mb(now.files)} MB of files, ${now.created} views made, ${now.awaiting} released not destroyed (${mb(now.awaitingMb)} MB)`;
  if (line === last) return;
  last = line;
  log?.say(line);
}
